import { Loader2, Upload } from "lucide-react";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useId,
  useMemo,
} from "react";
import { useDropzone } from "react-dropzone";

import { Button } from "@src/shared/components/ui/button";
import { cn } from "@src/shared/lib/utils";

const DEFAULT_ACCEPTED_FILE_TYPES = ".pdf,.png,.jpg,.jpeg,.csv,.txt";

export interface DocumentDropzoneProps {
  disabled?: boolean;
  isUploading?: boolean;
  multiple?: boolean;
  acceptedFileTypes?: string;
  label?: string;
  uploadingLabel?: string;
  hint?: string;
  ariaLabel?: string;
  className?: string;
  contentClassName?: string;
  children?: ReactNode;
  onFilesSelected?: (files: File[]) => void;
}

export interface DocumentDropzoneTriggerRenderProps {
  acceptedFileTypes: string;
  disabled: boolean;
  isDragging: boolean;
  isUploading: boolean;
  multiple: boolean;
  openFilePicker: () => void;
  triggerProps: {
    type: "button";
    disabled: boolean;
    "aria-label": string;
    "aria-describedby": string;
    onClick: () => void;
  };
}

export interface DocumentDropzoneTriggerProps {
  ariaLabel?: string;
  children: (props: DocumentDropzoneTriggerRenderProps) => ReactNode;
}

interface DocumentDropzoneContextValue {
  acceptedFileTypes: string;
  ariaLabel: string;
  hintId: string;
  isDisabled: boolean;
  isDragging: boolean;
  isUploading: boolean;
  multiple: boolean;
  openFilePicker: () => void;
}

interface DocumentDropzoneContentProps extends DocumentDropzoneProps {
  acceptedFileTypes: string;
  ariaLabel: string;
  hint: string;
  isDisabled: boolean;
  label: string;
  multiple: boolean;
  uploadingLabel: string;
}

const DocumentDropzoneContext =
  createContext<DocumentDropzoneContextValue | null>(null);

function useDocumentDropzoneContext(): DocumentDropzoneContextValue {
  const context = useContext(DocumentDropzoneContext);

  if (!context) {
    throw new Error(
      "DocumentDropzoneTrigger must be rendered inside DocumentDropzone.",
    );
  }

  return context;
}

export function DocumentDropzone({
  disabled = false,
  isUploading = false,
  multiple = true,
  acceptedFileTypes = DEFAULT_ACCEPTED_FILE_TYPES,
  label = "Drop a document",
  uploadingLabel = "Uploading document...",
  hint = "Drop a PDF, PNG, JPG, CSV, or TXT document here, or click to open the file picker.",
  ariaLabel = "Drop documents",
  className,
  contentClassName,
  children,
  onFilesSelected,
}: DocumentDropzoneProps) {
  const isDisabled = disabled || isUploading;

  return (
    <DocumentDropzoneContent
      acceptedFileTypes={acceptedFileTypes}
      ariaLabel={ariaLabel}
      className={className}
      contentClassName={contentClassName}
      disabled={disabled}
      hint={hint}
      isDisabled={isDisabled}
      isUploading={isUploading}
      label={label}
      multiple={multiple}
      uploadingLabel={uploadingLabel}
      onFilesSelected={onFilesSelected}
    >
      {children}
    </DocumentDropzoneContent>
  );
}

export function DocumentDropzoneTrigger({
  ariaLabel,
  children,
}: DocumentDropzoneTriggerProps) {
  const {
    acceptedFileTypes,
    ariaLabel: contextAriaLabel,
    hintId,
    isDisabled,
    isDragging,
    isUploading,
    multiple,
    openFilePicker,
  } = useDocumentDropzoneContext();
  const triggerProps = useMemo<DocumentDropzoneTriggerRenderProps>(
    () => ({
      acceptedFileTypes,
      disabled: isDisabled,
      isDragging,
      isUploading,
      multiple,
      openFilePicker,
      triggerProps: {
        type: "button",
        disabled: isDisabled,
        "aria-label": ariaLabel ?? contextAriaLabel,
        "aria-describedby": hintId,
        onClick: openFilePicker,
      },
    }),
    [
      acceptedFileTypes,
      ariaLabel,
      contextAriaLabel,
      hintId,
      isDisabled,
      isDragging,
      isUploading,
      multiple,
      openFilePicker,
    ],
  );

  return <>{children(triggerProps)}</>;
}

function DocumentDropzoneContent({
  isUploading = false,
  acceptedFileTypes,
  label,
  uploadingLabel,
  hint,
  ariaLabel,
  className,
  contentClassName,
  children,
  isDisabled,
  multiple,
  onFilesSelected,
}: DocumentDropzoneContentProps) {
  const hintId = useId();
  const visibleLabel = isUploading ? uploadingLabel : label;

  const handleDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (isDisabled || !acceptedFiles.length) return;
      onFilesSelected?.(multiple ? acceptedFiles : acceptedFiles.slice(0, 1));
    },
    [isDisabled, multiple, onFilesSelected],
  );
  const { getInputProps, getRootProps, isDragActive, open } = useDropzone({
    disabled: isDisabled,
    multiple: true,
    noClick: true,
    noKeyboard: true,
    onDrop: handleDrop,
  });
  const isActiveDropTarget = isDragActive;

  const contextValue = useMemo<DocumentDropzoneContextValue>(
    () => ({
      acceptedFileTypes,
      ariaLabel,
      hintId,
      isDisabled,
      isDragging: isActiveDropTarget,
      isUploading,
      multiple,
      openFilePicker: open,
    }),
    [
      acceptedFileTypes,
      ariaLabel,
      hintId,
      isActiveDropTarget,
      isDisabled,
      isUploading,
      multiple,
      open,
    ],
  );

  return (
    <DocumentDropzoneContext.Provider value={contextValue}>
      <div
        {...getRootProps({
          className: cn("relative overflow-hidden", className),
        })}
        data-document-dropzone=""
      >
        <div
          className={cn(
            "transition-[filter,opacity] duration-150",
            isActiveDropTarget && "opacity-70 blur-sm",
            contentClassName,
          )}
        >
          {children}
        </div>
        {isActiveDropTarget ? (
          <DropOverlay
            ariaDescribedBy={hintId}
            ariaLabel={ariaLabel}
            hint={hint}
            isUploading={isUploading}
            label={visibleLabel}
            onClick={open}
          />
        ) : null}
        <span id={hintId} className="sr-only">
          {hint}
        </span>
        <input
          {...getInputProps({
            accept: acceptedFileTypes,
            "aria-label": `${ariaLabel} file picker`,
            className: "sr-only",
            multiple,
            tabIndex: -1,
          })}
        />
      </div>
    </DocumentDropzoneContext.Provider>
  );
}

function DropOverlay({
  ariaDescribedBy,
  ariaLabel,
  hint,
  isUploading,
  label,
  onClick,
}: {
  ariaDescribedBy: string;
  ariaLabel: string;
  hint: string;
  isUploading: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
      className="absolute inset-0 z-20 h-full w-full rounded-none border-0 bg-primary/10 px-6 py-8 text-center shadow-none backdrop-blur-[3px] hover:bg-primary/10 focus-visible:ring-0"
      onClick={onClick}
    >
      <span className="pointer-events-none flex w-[min(360px,calc(100vw-48px))] flex-col items-center rounded-[18px] border-2 border-dashed border-primary/70 bg-background px-10 py-8 shadow-[0_22px_70px_rgba(15,23,42,0.18)]">
        <span className="mb-4 inline-flex size-12 items-center justify-center rounded-[13px] bg-primary/10 text-primary">
          {isUploading ? (
            <Loader2 className="size-5 animate-spin" aria-hidden="true" />
          ) : (
            <Upload className="size-5" aria-hidden="true" />
          )}
        </span>
        <span className="text-base font-semibold text-foreground">{label}</span>
        <span className="mt-3 text-sm leading-5 text-muted-foreground">
          {hint}
        </span>
      </span>
    </Button>
  );
}
